'use client'

import { useState, useCallback } from 'react'
import { useRouter } from '@/lib/next-shim'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { PhoneXMarkIcon } from '@heroicons/react/24/outline'
import { useMeetingManager } from 'amazon-chime-sdk-component-library-react'

interface EndConsultationControlProps {
  consultationId: string
  redirectUrl?: string
}

export function EndConsultationControl({ consultationId, redirectUrl }: EndConsultationControlProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [isEnding, setIsEnding] = useState(false)
  const router = useRouter()
  const meetingManager = useMeetingManager()

  const handleEndConsultation = useCallback(async () => {
    setIsEnding(true)
    try {
      localStorage.removeItem(`patient_data_reuse_consent_${consultationId}`)

      // Leave the Chime meeting
      await meetingManager.leave()

      // Redirect to consultation end page or provided URL
      const redirect = redirectUrl || `/account/consultations/${consultationId}`
      router.push(redirect)
    } catch (error) {
      console.error('Error ending consultation:', error)
      setIsEnding(false)
      setShowConfirmDialog(false)
    }
  }, [meetingManager, router, consultationId, redirectUrl])

  return (
    <>
      {/* End Consultation Button */}
      <div className="ds-tooltip" data-tip={t`End consultation`}>
        <button
          type="button"
          className="flex rounded-full bg-red-600 p-2 text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors"
          onClick={() => setShowConfirmDialog(true)}
          aria-label={t`End consultation`}
        >
          <PhoneXMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => !isEnding && setShowConfirmDialog(false)} aria-hidden="true" />

          {/* Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 z-10">
            <div className="text-center">
              {/* Icon */}
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 mb-4">
                <PhoneXMarkIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                <Trans>End consultation?</Trans>
              </h3>

              {/* Description */}
              <p className="text-sm text-gray-500 mb-6">
                <Trans>Are you sure you want to end this teleconsultation? You will be able to find the consultation documents in your patient area.</Trans>
              </p>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-center">
                <button
                  type="button"
                  className="inline-flex justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setShowConfirmDialog(false)}
                  disabled={isEnding}
                >
                  <Trans>Cancel</Trans>
                </button>
                <button
                  type="button"
                  className="inline-flex justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleEndConsultation}
                  disabled={isEnding}
                >
                  {isEnding ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <Trans>Closing...</Trans>
                    </>
                  ) : (
                    <Trans>Yes, end</Trans>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
